import React, { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { Box, FormControl, InputLabel, Select, SelectChangeEvent, Tabs, Tab, Typography, MenuItem } from '@mui/material';
import { Item, DataRow, UnitType, DenominatorType } from './types';
import { convertUnits } from './Chart.Common'; // Make sure the path is correct
import { formatLocaleNumber } from '../../utils/string';

interface CannabinoidsViewProps {
    items: Item<DataRow>[];
    selectedUnit: UnitType;
    selectedDenominator: DenominatorType;
    selectedProducts: string[];
}

const CannabinoidsView: React.FC<CannabinoidsViewProps> = ({ items, selectedUnit, selectedDenominator, selectedProducts }) => {
    const [mode, setMode] = useState<'levels' | 'decarboxylation' | 'total'>('levels');
    const [viewTab, setViewTab] = useState<'chart' | 'table'>('chart');

    const convertedItems = useMemo(() => {
        return convertUnits(items, selectedUnit, selectedDenominator);
    }, [items, selectedUnit, selectedDenominator]);

    const productData = useMemo(() => {
        const dataByProduct = new Map<string, any>();

        const compounds = [
            'THC', 'THCA', 'THCAA', 'CBD', 'CBDA', 'CBG', 'CBGA', 'CBDV', 'CBDVA', 'CBC', 'CBCA'
        ];

        convertedItems.forEach(item => {
            item.DATA.forEach((row: DataRow) => {
                const productKey = `${row.PRODUCT || 'Unknown'} (${row.ANALYSIS_TYPE || 'Unknown'})`;

                if (selectedProducts.length > 0 && !selectedProducts.includes(productKey)) {
                    return;
                }

                if (!dataByProduct.has(productKey)) {
                    dataByProduct.set(productKey, {
                        THC: 0, THCA: 0, THCAA: 0, CBD: 0, CBDA: 0, CBG: 0, CBGA: 0,
                        CBDV: 0, CBDVA: 0, CBC: 0, CBCA: 0,
                        totalTHC: 0, totalCBD: 0, totalCBG: 0, totalCBDV: 0, totalCBC: 0,
                        totalCannabinoids: 0,
                        decarbTHC: 0, decarbCBD: 0, decarbCBG: 0, decarbCBDV: 0, decarbCBC: 0
                    });
                }

                const data = dataByProduct.get(productKey)!;
                const value = typeof row.AVERAGE === 'number' ? row.AVERAGE :
                    typeof row.AVERAGE === 'string' && row.AVERAGE.includes('Q') ? row.LOQ ?? 0 :
                        typeof row.AVERAGE === 'string' && row.AVERAGE.includes('D') ? row.LOD ?? 0 : 0;

                const name = row.COMPOUND_NAME || '';
                for (const compound of compounds) {
                    const regex = new RegExp(`\\b${compound}\\b(?!\\w)`, 'i');
                    if (regex.test(name)) {
                        data[compound] = value;
                        break;
                    }
                }
            });
        });

        const result = Array.from(dataByProduct.entries()).map(([product, data]) => {
            data.totalTHC = data.THC !== 0 && (data.THCA !== 0 || data.THCAA !== 0)
                ? data.THC + (data.THCA !== 0 ? data.THCA : data.THCAA) * 0.877 : 0;
            data.totalCBD = data.CBD !== 0 && data.CBDA !== 0 ? data.CBD + data.CBDA * 0.877 : 0;
            data.totalCBG = data.CBG !== 0 && data.CBGA !== 0 ? data.CBG + data.CBGA * 0.878 : 0;
            data.totalCBDV = data.CBDV !== 0 && data.CBDVA !== 0 ? data.CBDV + data.CBDVA * 0.867 : 0;
            data.totalCBC = data.CBC !== 0 && data.CBCA !== 0 ? data.CBC + data.CBCA * 0.87 : 0;
            data.totalCannabinoids = data.totalTHC + data.totalCBD + data.totalCBG + data.totalCBDV + data.totalCBC;

            data.decarbTHC = data.THC !== 0 && (data.THCA !== 0 || data.THCAA !== 0)
                ? (data.THC / (data.THC + (data.THCA !== 0 ? data.THCA : data.THCAA))) * 100 : 0;
            data.decarbCBD = data.CBD !== 0 && data.CBDA !== 0 ? (data.CBD / (data.CBD + data.CBDA)) * 100 : 0;
            data.decarbCBG = data.CBG !== 0 && data.CBGA !== 0 ? (data.CBG / (data.CBG + data.CBGA)) * 100 : 0;
            data.decarbCBDV = data.CBDV !== 0 && data.CBDVA !== 0 ? (data.CBDV / (data.CBDV + data.CBDVA)) * 100 : 0;
            data.decarbCBC = data.CBC !== 0 && data.CBCA !== 0 ? (data.CBC / (data.CBC + data.CBCA)) * 100 : 0;

            return { product, ...data };
        }).sort((a, b) => a.product.localeCompare(b.product));

        return result;
    }, [convertedItems, selectedProducts]);

    const handleModeChange = (event: SelectChangeEvent<'levels' | 'decarboxylation' | 'total'>) => {
        setMode(event.target.value as 'levels' | 'decarboxylation' | 'total');
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: 'chart' | 'table') => {
        setViewTab(newValue);
    };

    const chartData = useMemo(() => {
        const labels = productData.map(d => d.product);
        let datasets: any[] = [];

        if (mode === 'levels') {
            datasets = [
                { label: 'Total THC', data: productData.map(d => d.totalTHC), backgroundColor: 'rgba(255, 99, 132, 0.5)' },
                { label: 'Total CBD', data: productData.map(d => d.totalCBD), backgroundColor: 'rgba(54, 162, 235, 0.5)' },
                { label: 'Total CBG', data: productData.map(d => d.totalCBG), backgroundColor: 'rgba(255, 206, 86, 0.5)' },
                { label: 'Total CBDV', data: productData.map(d => d.totalCBDV), backgroundColor: 'rgba(75, 192, 192, 0.5)' },
                { label: 'Total CBC', data: productData.map(d => d.totalCBC), backgroundColor: 'rgba(153, 102, 255, 0.5)' }
            ];
        } else if (mode === 'decarboxylation') {
            datasets = [
                { label: 'Decarb THC %', data: productData.map(d => d.decarbTHC), backgroundColor: 'rgba(255, 99, 132, 0.5)' },
                { label: 'Decarb CBD %', data: productData.map(d => d.decarbCBD), backgroundColor: 'rgba(54, 162, 235, 0.5)' },
                { label: 'Decarb CBG %', data: productData.map(d => d.decarbCBG), backgroundColor: 'rgba(255, 206, 86, 0.5)' },
                { label: 'Decarb CBDV %', data: productData.map(d => d.decarbCBDV), backgroundColor: 'rgba(75, 192, 192, 0.5)' },
                { label: 'Decarb CBC %', data: productData.map(d => d.decarbCBC), backgroundColor: 'rgba(153, 102, 255, 0.5)' }
            ];
        } else {
            datasets = [
                { label: 'Total Cannabinoids', data: productData.map(d => d.totalCannabinoids), backgroundColor: 'rgba(255, 159, 64, 0.5)' }
            ];
        }

        return { labels, datasets };
    }, [productData, mode]);

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: { position: 'top' as const },
            title: {
                display: true,
                text: mode === 'levels'
                    ? `Total Cannabinoid Levels (${selectedUnit}/${selectedDenominator})`
                    : mode === 'decarboxylation'
                        ? 'Decarboxylation Levels (%)'
                        : `Total Cannabinoids (${selectedUnit}/${selectedDenominator})`
            }
        },
        scales: {
            x: { title: { display: true, text: 'Products' } },
            y: {
                title: {
                    display: true,
                    text: mode === 'decarboxylation' ? 'Percentage (%)' : `Value (${selectedUnit}/${selectedDenominator})`
                },
                beginAtZero: true
            }
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Analysis Mode</InputLabel>
                    <Select
                        value={mode}
                        onChange={handleModeChange}
                        label="Analysis Mode"
                        size="small"
                    >
                        <MenuItem value="levels">Total Cannabinoid Levels</MenuItem>
                        <MenuItem value="decarboxylation">Total Decarboxylation Levels %</MenuItem>
                        <MenuItem value="total">Total Cannabinoids</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={viewTab} onChange={handleTabChange} aria-label="cannabinoid view tabs">
                    <Tab label="Bar Chart" value="chart" />
                    <Tab label="Table" value="table" />
                </Tabs>
            </Box>
            {viewTab === 'chart' ? (
                <Box sx={{ p: 2, maxHeight: 590 }}>
                    {productData.length > 0 ? (
                        <Bar data={chartData} options={chartOptions} />
                    ) : (
                        <Typography>No cannabinoid data available for selected products</Typography>
                    )}
                </Box>
            ) : (
                <Box sx={{ p: 2, maxHeight: 590 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                        <tr style={{ borderBottom: '1px solid #ddd' }}>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Product</th>
                            {mode === 'levels' ? (
                                <>
                                    <th style={{ padding: '8px', textAlign: 'right' }}>Total THC ({selectedUnit}/{selectedDenominator})</th>
                                    <th style={{ padding: '8px', textAlign: 'right' }}>Total CBD ({selectedUnit}/{selectedDenominator})</th>
                                    <th style={{ padding: '8px', textAlign: 'right' }}>Total CBG ({selectedUnit}/{selectedDenominator})</th>
                                    <th style={{ padding: '8px', textAlign: 'right' }}>Total CBDV ({selectedUnit}/{selectedDenominator})</th>
                                    <th style={{ padding: '8px', textAlign: 'right' }}>Total CBC ({selectedUnit}/{selectedDenominator})</th>
                                </>
                            ) : mode === 'decarboxylation' ? (
                                <>
                                    <th style={{ padding: '8px', textAlign: 'right' }}>Decarb THC (%)</th>
                                    <th style={{ padding: '8px', textAlign: 'right' }}>Decarb CBD (%)</th>
                                    <th style={{ padding: '8px', textAlign: 'right' }}>Decarb CBG (%)</th>
                                    <th style={{ padding: '8px', textAlign: 'right' }}>Decarb CBDV (%)</th>
                                    <th style={{ padding: '8px', textAlign: 'right' }}>Decarb CBC (%)</th>
                                </>
                            ) : (
                                <th style={{ padding: '8px', textAlign: 'right' }}>Total Cannabinoids ({selectedUnit}/{selectedDenominator})</th>
                            )}
                        </tr>
                        </thead>
                        <tbody>
                        {productData.length > 0 ? (
                            productData.map((data) => (
                                <tr key={data.product} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '8px' }}>{data.product}</td>
                                    {mode === 'levels' ? (
                                        <>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(data.totalTHC)}</td>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(data.totalCBD)}</td>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(data.totalCBG)}</td>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(data.totalCBDV)}</td>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(data.totalCBC)}</td>
                                        </>
                                    ) : mode === 'decarboxylation' ? (
                                        <>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(data.decarbTHC)}</td>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(data.decarbCBD)}</td>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(data.decarbCBG)}</td>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(data.decarbCBDV)}</td>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(data.decarbCBC)}</td>
                                        </>
                                    ) : (
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(data.totalCannabinoids)}</td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={mode === 'total' ? 2 : 6} style={{ padding: '8px', textAlign: 'center' }}>
                                    No data available for selected products
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </Box>
            )}
        </Box>
    );
};

export default CannabinoidsView;
